import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { discoverJmapOrigin } from "./core/discovery";
import type { ShellClient } from "./core/shell";
import type { MailShellSnapshot, ShellOpenConfig, ShellOpenResult } from "./core/shell-types";
import {
  beginSSO,
  completeSSO,
  discoverSignIn,
  type OIDCSession,
  renewSSO,
  validateConfiguration,
} from "./oidc";
import {
  createBrowserVault,
  createTaskQueue,
  type EnrollPasskeyResult,
  type SealedAccount,
  type SealedAccountsPayload,
  type Vault,
  VaultError,
  type VaultProtection,
} from "./vault";

// Pre-vault account list (plaintext Basic header in localStorage). Read once,
// sealed into the vault, deleted. Kept only for that migration.
export const LEGACY_STORAGE_KEY = "lately.web.accounts.v1";
const LEGACY_SESSION_KEY = "lately.web.session";

// Where the shared core keeps its SQLite state inside the worker's WASI
// filesystem. "/lately" is the durable (OPFS-backed) subtree; the path matches
// AppDatabase.defaultURL on WASI so the same file is used whichever side
// derives it.
export const SHELL_STORE_PATH = "/lately/Lately/state.sqlite";

// Development knob: `?store=memory` in the URL (or localStorage
// `lately.web.store` = "memory") opens the session without a storePath, so the
// core keeps its SQLite state in memory and touches no filesystem at all —
// useful against a core build whose WASI shim has no durable filesystem yet.
export function shellStorePath(): string | undefined {
  if (typeof window === "undefined") {
    return SHELL_STORE_PATH;
  }
  const fromQuery = new URLSearchParams(window.location.search).get("store");
  const fromStorage = (() => {
    try {
      return localStorage.getItem("lately.web.store");
    } catch {
      return null;
    }
  })();
  return (fromQuery ?? fromStorage ?? "").toLowerCase() === "memory" ? undefined : SHELL_STORE_PATH;
}

// Lately is fully client-side: the shared Swift core (in a worker) fetches the
// JMAP origin directly, so the server must answer CORS. Sign-in uses Basic or OAuth;
// the secret rests only inside the vault (src/vault: AES-GCM under the DEK
// that a lately.org passkey's PRF or the browser's session key unwraps) and,
// when the core's store is encrypted, in the core's `secrets` table. Several
// accounts can exist at once; the active one is the session the shell has open.
export interface StoredAccount {
  origin: string;
  authorization: string;
  username: string;
  oidc?: OIDCSession;
}

interface StoredState {
  accounts: StoredAccount[];
  active: number;
}

export type SessionStatus =
  | { status: "idle" }
  | { status: "opening" }
  | { status: "open"; accountId: string | null }
  | { status: "failed"; error: string };

// The vault as the UI sees it. `starting`: the silent unlock is running.
// `locked`: a DEK exists but needs the passkey (unlockable) or is gone
// (reset only). `ready`: unlocked, or no vault yet (protection null — one is
// created at first sign-in).
export type VaultStatus =
  | { kind: "starting" }
  | { kind: "locked"; unlockable: boolean; error: string | null; busy: boolean }
  | { kind: "ready"; protection: VaultProtection | null; passkeyAvailable: boolean };

interface AuthContextValue {
  shell: ShellClient;
  accounts: StoredAccount[];
  activeIndex: number;
  active: StoredAccount | null;
  username: string | null;
  session: SessionStatus;
  connecting: boolean;
  error: string | null;
  addingAccount: boolean;
  setAddingAccount: (adding: boolean) => void;
  signInEmail: (email: string, password?: string) => Promise<"passwordRequired" | undefined>;
  signInBasic: (origin: string, username: string, password: string) => Promise<void>;
  switchAccount: (index: number) => void;
  removeAccount: (index: number) => void;
  // Multi-tab: take the durable (OPFS) storage over from the tab that has it
  // and re-open the active account here.
  takeoverStorage: () => Promise<void>;
  // The vault (docs/runbooks/lately-shared-auth.md).
  vault: VaultStatus;
  // Runs the passkey ceremony; call from a user gesture.
  unlockVault: () => Promise<void>;
  // Enrols a lately.org passkey for this vault; call from a user gesture.
  // Default (reuse-or-create): a discoverable assertion asks whichever
  // lately.org passkey the password manager already holds (synced from
  // another device) to evaluate the PRF and adds a slot under it — no new
  // passkey; one is created only when none answers. `mode: "create"`
  // registers a new passkey under the vault's identity, which REPLACES the
  // previous one in the password manager (the UI warns before calling it).
  enrollPasskey: (options?: {
    mode?: "reuse-or-create" | "create";
  }) => Promise<EnrollPasskeyResult>;
  // Forgets the browser session key: the next start needs the passkey.
  lockVault: () => Promise<void>;
  // Wipes the vault and the local mail cache; the user signs in again.
  resetVault: () => Promise<void>;
  // Whether the core's store is encrypted under the vault's key (null until
  // the first open reports it).
  storeEncrypted: boolean | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readLegacyStored(): StoredState | null {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredState;
      if (Array.isArray(parsed.accounts) && parsed.accounts.length > 0) {
        return { accounts: parsed.accounts, active: Math.max(0, parsed.active | 0) };
      }
    }
    const legacy = sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (legacy) {
      const single = JSON.parse(legacy) as StoredAccount;
      return { accounts: [single], active: 0 };
    }
  } catch {
    // fall through to null
  }
  return null;
}

function forgetLegacyStored() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // storage may be unavailable; nothing to forget then
  }
}

// StoredAccount <-> the vault's sealed roster. Authorization headers exist in
// memory; the vault seals passwords or OAuth tokens and renewal metadata.
export function toSealedAccounts(state: StoredState): SealedAccountsPayload {
  const accounts: SealedAccount[] = [];
  for (const account of state.accounts) {
    const decoded = decodeBasic(account.authorization);
    if (account.oidc) {
      accounts.push({
        origin: account.origin,
        username: account.username,
        credentialKind: "oidcSession",
        secret: JSON.stringify({ authorization: account.authorization, oidc: account.oidc }),
      });
    } else if (decoded) {
      accounts.push({
        origin: account.origin,
        username: account.username,
        credentialKind: "basicPassword",
        secret: decoded.password,
      });
    }
  }
  return { version: 1, accounts, active: Math.min(state.active, Math.max(0, accounts.length - 1)) };
}

export function fromSealedAccounts(payload: SealedAccountsPayload): StoredState {
  const accounts: StoredAccount[] = payload.accounts.map((account) => {
    if (account.credentialKind === "oidcSession") {
      const session = JSON.parse(account.secret);
      validateConfiguration(session.oidc);
      if (
        account.origin !== session.oidc.jmapOrigin ||
        !session.authorization?.startsWith("Bearer ") ||
        !Number.isFinite(session.oidc?.expiresAt)
      )
        throw new Error("Invalid saved sign-in.");
      return {
        origin: account.origin,
        username: account.username,
        authorization: session.authorization,
        oidc: session.oidc,
      };
    }
    if (account.credentialKind !== "basicPassword")
      throw new Error("Unsupported saved credential.");
    return {
      origin: account.origin,
      username: account.username,
      authorization: `Basic ${btoa(`${account.username}:${account.secret}`)}`,
    };
  });
  return {
    accounts,
    active: Math.min(Math.max(0, payload.active | 0), Math.max(0, accounts.length - 1)),
  };
}

function decodeBasic(authorization: string): { username: string; password: string } | null {
  const [scheme, value] = authorization.split(" ", 2);
  if (scheme?.toLowerCase() !== "basic" || !value) {
    return null;
  }
  const decoded = atob(value);
  const separator = decoded.indexOf(":");
  if (separator <= 0) {
    return null;
  }
  return { username: decoded.slice(0, separator), password: decoded.slice(separator + 1) };
}

function accountKey(account: StoredAccount): string {
  return JSON.stringify([account.origin, account.username, account.authorization]);
}

// Forward the active credential to Swift. `storeKey` is the vault's SQLite
// codec key for this open; refresh tokens stay in the browser vault.
export function shellConfigFor(account: StoredAccount, storeKey?: string): ShellOpenConfig {
  const decoded = decodeBasic(account.authorization);
  if (decoded) {
    return {
      serverURL: account.origin,
      username: decoded.username,
      password: decoded.password,
      storePath: shellStorePath(),
      storeKey,
    };
  }
  if (account.oidc && account.authorization.startsWith("Bearer ")) {
    return {
      serverURL: account.origin,
      username: account.username,
      bearerToken: account.authorization.slice(7),
      storePath: shellStorePath(),
      storeKey,
    };
  }
  throw new Error("Unsupported sign-in credential.");
}

// The core's StoredAccount.storageKey for a browser-side account: taken from
// the snapshot when the core already lists it, else derived the way
// StoredAccountRecord.storageKey does (`serverURL|credentialKind|username`,
// lower-cased), including the active credential kind.
export function shellStorageKeyFor(account: StoredAccount, snapshot: MailShellSnapshot): string {
  const origin = account.origin.toLowerCase();
  const username = account.username.toLowerCase();
  const listed = snapshot.accounts.find(
    (item) =>
      item.serverURL.toLowerCase().replace(/\/+$/, "") === origin.replace(/\/+$/, "") &&
      item.username.toLowerCase() === username,
  );
  return (
    listed?.storageKey ?? `${origin}|${account.oidc ? "bearerToken" : "basicPassword"}|${username}`
  );
}

function isUnauthorized(cause: unknown): boolean {
  const message = cause instanceof Error ? cause.message : String(cause);
  return /\b401\b|unauthori[sz]ed/i.test(message);
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function AuthProvider({
  shell,
  vault: vaultProp,
  children,
}: {
  shell: ShellClient;
  vault?: Vault;
  children: ReactNode;
}) {
  const [isOAuthCallback] = useState(() => window.location.pathname === "/auth/callback");
  const oauthCallbackStarted = useRef(false);
  const [vault] = useState<Vault>(() => vaultProp ?? createBrowserVault());
  const [stored, setStored] = useState<StoredState>({ accounts: [], active: 0 });
  const [vaultStatus, setVaultStatus] = useState<VaultStatus>({ kind: "starting" });
  const [storeEncrypted, setStoreEncrypted] = useState<boolean | null>(null);
  const [session, setSession] = useState<SessionStatus>({ status: "idle" });
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  // Serialises open/close against the single global shell session, and vault
  // mutations against each other: persist() shares this queue so a slow seal
  // cannot land after a later one and clobber fresher state.
  const queueRef = useRef(createTaskQueue());
  const openedKeyRef = useRef<string | null>(null);
  // The vault's store key for this unlock (base64url); null while locked.
  const storeKeyRef = useRef<string | null>(null);
  // Bumped to make the effect below re-open the active account after a failed
  // sign-in attempt replaced its session, or after an unlock.
  const [reopenTick, setReopenTick] = useState(0);

  const active = stored.accounts[stored.active] ?? null;
  const vaultReady = vaultStatus.kind === "ready";
  // What the shell session should currently be: the active account, plus an
  // attempt counter so a failed sign-in can force a re-open of the same one.
  // Nothing opens while the vault is locked (no store key, no credentials).
  const sessionTarget = useMemo(
    () => (active && vaultReady ? { account: active, attempt: reopenTick } : null),
    [active, vaultReady, reopenTick],
  );

  const enqueue = useCallback(
    <T,>(task: () => Promise<T>): Promise<T> => queueRef.current(task),
    [],
  );

  const openSession = useCallback(
    (config: ShellOpenConfig) =>
      enqueue(async () => {
        await shell.close().catch(() => {});
        return shell.open(config);
      }),
    [enqueue, shell],
  );

  const noteOpenResult = useCallback((result: ShellOpenResult) => {
    if (typeof result.storeEncrypted === "boolean") {
      setStoreEncrypted(result.storeEncrypted);
    }
  }, []);

  // Persist the roster: sealed into the vault (never localStorage), serialised
  // through the same queue as every other vault mutation so a slow seal
  // cannot land after a later one and clobber fresher state. The unlock is
  // re-checked inside the task: a lockVault that queues first wins, and this
  // seal is skipped instead of throwing on a locked vault. Failures are
  // logged, not thrown — the in-memory state is what the session runs on.
  // The returned promise lets callers/tests await the actual write.
  const persist = useCallback(
    (state: StoredState) =>
      enqueue(async () => {
        if (!vault.isUnlocked) {
          return;
        }
        try {
          await vault.sealAccounts(toSealedAccounts(state));
        } catch (cause) {
          console.warn("Lately: sealing the account list failed", cause);
        }
      }),
    [enqueue, vault],
  );

  // Publishes the unlocked vault: derives the store key, loads the sealed
  // roster (or migrates the pre-vault localStorage list) and flips to ready.
  const publishUnlocked = useCallback(
    async (protection: VaultProtection | null) => {
      storeKeyRef.current = vault.isUnlocked ? await vault.storeKey() : null;
      let next: StoredState = { accounts: [], active: 0 };
      if (vault.isUnlocked) {
        try {
          const sealed = await vault.openAccounts();
          if (sealed) {
            next = fromSealedAccounts(sealed);
          }
        } catch (cause) {
          console.warn("Lately: the sealed account list is unreadable; starting empty", cause);
        }
      }
      setStored(next);
      setVaultStatus({
        kind: "ready",
        protection,
        passkeyAvailable: await vault.passkeyAvailable(),
      });
    },
    [vault],
  );

  // Boot: silent unlock (session key), else migrate the legacy list into a
  // fresh vault, else surface the locked state for the Unlock/Reset screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let state = await vault.unlock({ interactive: false });
      if (state.status === "empty") {
        const legacy = readLegacyStored();
        if (legacy) {
          try {
            state = await vault.create();
          } catch (cause) {
            // A concurrent mount (StrictMode) created it first.
            if (!(cause instanceof VaultError && cause.code === "already-exists")) {
              throw cause;
            }
            state = await vault.unlock({ interactive: false });
          }
          if (state.status === "unlocked") {
            await vault.sealAccounts(toSealedAccounts(legacy));
            forgetLegacyStored();
          }
        }
      }
      if (cancelled) {
        return;
      }
      if (state.status === "unlocked") {
        await publishUnlocked(state.protection);
      } else if (state.status === "empty") {
        await publishUnlocked(null);
      } else {
        storeKeyRef.current = null;
        setVaultStatus({ kind: "locked", unlockable: state.unlockable, error: null, busy: false });
      }
    })().catch((cause) => {
      if (!cancelled) {
        console.warn("Lately: vault start failed", cause);
        setVaultStatus({ kind: "locked", unlockable: false, error: describe(cause), busy: false });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [vault, publishUnlocked]);

  // Keep the shell session in step with the active stored account.
  useEffect(() => {
    if (!sessionTarget) {
      if (openedKeyRef.current !== null) {
        openedKeyRef.current = null;
        void enqueue(() => shell.close().catch(() => ({ ok: true as const })));
      }
      setSession({ status: "idle" });
      return;
    }
    const key = accountKey(sessionTarget.account);
    if (openedKeyRef.current === key) {
      return;
    }
    openedKeyRef.current = key;
    setSession({ status: "opening" });
    let config: ShellOpenConfig;
    try {
      config = shellConfigFor(sessionTarget.account, storeKeyRef.current ?? undefined);
    } catch (cause) {
      setSession({ status: "failed", error: describe(cause) });
      return;
    }
    // No cancellation flag: StrictMode re-runs this effect once, and the ref
    // guard above makes the re-run a no-op, so the settle below must not be
    // tied to a particular run. A stale settle (account switched meanwhile)
    // is ignored by comparing keys instead.
    openSession(config)
      .then((result) => {
        if (openedKeyRef.current === key) {
          noteOpenResult(result);
          setSession({ status: "open", accountId: result.accountId ?? null });
        }
      })
      .catch((cause) => {
        if (openedKeyRef.current === key) {
          setSession({ status: "failed", error: describe(cause) });
        }
      });
  }, [sessionTarget, enqueue, openSession, shell, noteOpenResult]);

  const adopt = useCallback(
    (account: StoredAccount) => {
      setStored((current) => {
        const existing = current.accounts.findIndex(
          (item) => item.origin === account.origin && item.username === account.username,
        );
        const accounts =
          existing >= 0
            ? current.accounts.map((item, index) => (index === existing ? account : item))
            : [...current.accounts, account];
        const next = { accounts, active: existing >= 0 ? existing : accounts.length - 1 };
        persist(next);
        return next;
      });
      setAddingAccount(false);
    },
    [persist],
  );

  // A first sign-in on a fresh profile creates the vault (SK-only until the
  // user enrols a passkey). Idempotent.
  const ensureVault = useCallback(async () => {
    if (vault.isUnlocked) {
      return;
    }
    let state = await vault.unlock({ interactive: false });
    if (state.status === "empty") {
      try {
        state = await vault.create();
      } catch (cause) {
        if (!(cause instanceof VaultError && cause.code === "already-exists")) {
          throw cause;
        }
        state = await vault.unlock({ interactive: false });
      }
    }
    if (state.status !== "unlocked") {
      throw new VaultError("locked", "Unlock Lately before signing in.");
    }
    storeKeyRef.current = await vault.storeKey();
    setVaultStatus({
      kind: "ready",
      protection: state.protection,
      passkeyAvailable: await vault.passkeyAvailable(),
    });
  }, [vault]);

  const connectAndStore = useCallback(
    async (origin: string, user: string, password: string) => {
      await ensureVault();
      const account: StoredAccount = {
        origin,
        authorization: `Basic ${btoa(`${user}:${password}`)}`,
        username: user,
      };
      let result: ShellOpenResult;
      try {
        result = await openSession({
          serverURL: origin,
          username: user,
          password,
          storePath: shellStorePath(),
          storeKey: storeKeyRef.current ?? undefined,
        });
      } catch (cause) {
        // The attempt displaced whatever session was open; put it back.
        openedKeyRef.current = null;
        setReopenTick((tick) => tick + 1);
        throw cause;
      }
      openedKeyRef.current = accountKey(account);
      noteOpenResult(result);
      setSession({ status: "open", accountId: result.accountId ?? null });
      adopt(account);
    },
    [adopt, ensureVault, noteOpenResult, openSession],
  );

  useEffect(() => {
    if (!isOAuthCallback || !vaultReady || oauthCallbackStarted.current) return;
    oauthCallbackStarted.current = true;
    setConnecting(true);
    completeSSO()
      .then(async (login) => {
        await ensureVault();
        const account: StoredAccount = {
          origin: login.session.jmapOrigin,
          username: login.username,
          authorization: `Bearer ${login.accessToken}`,
          oidc: login.session,
        };
        const result = await openSession(shellConfigFor(account, storeKeyRef.current ?? undefined));
        openedKeyRef.current = accountKey(account);
        noteOpenResult(result);
        setSession({ status: "open", accountId: result.accountId ?? null });
        adopt(account);
      })
      .catch((cause) => {
        setError(describe(cause));
        openedKeyRef.current = null;
        setReopenTick((tick) => tick + 1);
      })
      .finally(() => setConnecting(false));
  }, [isOAuthCallback, vaultReady, ensureVault, openSession, noteOpenResult, adopt]);

  // Rotation stays in the vault. Reopening propagates the new token to Swift;
  // focus/visibility also catch renewal after a suspended browser resumes.
  useEffect(() => {
    if (!active?.oidc || !vaultReady) return;
    const oidc = active.oidc;
    let cancelled = false;
    let renewing = false;
    let timer: ReturnType<typeof setTimeout>;
    const renew = async () => {
      if (cancelled || renewing || Date.now() < oidc.expiresAt - 60_000) return;
      renewing = true;
      try {
        const tokens = await renewSSO(oidc);
        if (cancelled) return;
        setStored((current) => {
          const accounts = current.accounts.map((account) =>
            account === active
              ? { ...account, authorization: `Bearer ${tokens.accessToken}`, oidc: tokens.session }
              : account,
          );
          const next = { ...current, accounts };
          persist(next);
          return next;
        });
      } catch (cause) {
        if (!cancelled) {
          setError(describe(cause));
          setSession({ status: "failed", error: describe(cause) });
          setAddingAccount(true);
        }
      }
    };
    timer = setTimeout(
      () => void renew(),
      Math.max(0, active.oidc.expiresAt - Date.now() - 60_000),
    );
    const wake = () => void renew();
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [active, vaultReady, persist]);

  const signInBasic = useCallback(
    async (origin: string, user: string, password: string) => {
      setConnecting(true);
      setError(null);
      try {
        await connectAndStore(origin.replace(/\/+$/, "") || window.location.origin, user, password);
      } catch (cause) {
        setError(describe(cause));
        throw cause;
      } finally {
        setConnecting(false);
      }
    },
    [connectAndStore],
  );

  const signInEmail = useCallback(
    async (email: string, password?: string): Promise<"passwordRequired" | undefined> => {
      setConnecting(true);
      setError(null);
      try {
        if (password === undefined) {
          const configuration = await discoverSignIn(email);
          if (configuration) {
            await beginSSO(configuration, email);
            return;
          }
          return "passwordRequired";
        }
        const origin = await discoverJmapOrigin(email);
        try {
          await connectAndStore(origin, email, password);
        } catch (cause) {
          // Some servers key logins on the account name rather than the full
          // address (Stalwart's Authentik-LDAP binding uses the identity slug).
          const localPart = email.split("@")[0];
          if (isUnauthorized(cause) && localPart) {
            await connectAndStore(origin, localPart, password);
          } else {
            throw cause;
          }
        }
      } catch (cause) {
        setError(describe(cause));
        throw cause;
      } finally {
        setConnecting(false);
      }
    },
    [connectAndStore],
  );

  const switchAccount = useCallback(
    (index: number) => {
      setError(null);
      setStored((current) => {
        if (index < 0 || index >= current.accounts.length || index === current.active) {
          return current;
        }
        const next = { ...current, active: index };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  // Sign-out: forget the account here, tell the core to drop its stored
  // account (which disconnects when it is the active one), and when nothing
  // is left, wipe the worker's on-disk state and the vault so no mail and no
  // key stays behind. The session effect above closes or re-targets the
  // shell session behind this (it runs on the next commit, so it queues
  // after these calls).
  const removeAccount = useCallback(
    (index: number) => {
      setError(null);
      const target = stored.accounts[index];
      if (!target) {
        return;
      }
      const storageKey = shellStorageKeyFor(target, shell.snapshot);
      const isLast = stored.accounts.length === 1;
      setStored((current) => {
        const accounts = current.accounts.filter((_, i) => i !== index);
        const activeIndex =
          current.active === index
            ? Math.max(0, current.active - 1)
            : current.active > index
              ? current.active - 1
              : current.active;
        const next = { accounts, active: Math.min(activeIndex, Math.max(0, accounts.length - 1)) };
        if (!isLast) {
          persist(next);
        }
        return next;
      });
      void enqueue(async () => {
        try {
          await shell.command({ op: "deleteAccount", storageKey });
        } catch (cause) {
          // Older cores lack the op and an evicted tab has no core; the wipe
          // below (last account) or the next open still leaves nothing usable.
          console.warn("Lately: deleteAccount failed", cause);
        }
        if (isLast) {
          try {
            await shell.wipe();
          } catch (cause) {
            console.warn("Lately: storage wipe failed", cause);
          }
          // The vault goes with the last account: the DEK keyed a store that
          // no longer exists. The passkey stays in the password manager and
          // is replaced on the next enrolment.
          try {
            await vault.reset();
          } catch (cause) {
            console.warn("Lately: vault reset failed", cause);
          }
          storeKeyRef.current = null;
          setStoreEncrypted(null);
          setVaultStatus({
            kind: "ready",
            protection: null,
            passkeyAvailable: await vault.passkeyAvailable(),
          });
        }
      });
    },
    [enqueue, persist, shell, stored.accounts, vault],
  );

  const takeoverStorage = useCallback(
    () =>
      enqueue(async () => {
        await shell.takeover();
        // The worker re-instantiated its core on the stolen pool; whatever
        // session was open is gone, so make the effect above open it again.
        openedKeyRef.current = null;
        setReopenTick((tick) => tick + 1);
      }),
    [enqueue, shell],
  );

  const unlockVault = useCallback(async () => {
    setVaultStatus((current) =>
      current.kind === "locked" ? { ...current, busy: true, error: null } : current,
    );
    try {
      const state = await vault.unlock({ interactive: true });
      if (state.status === "unlocked") {
        await publishUnlocked(state.protection);
        openedKeyRef.current = null;
        setReopenTick((tick) => tick + 1);
      } else if (state.status === "empty") {
        await publishUnlocked(null);
      } else {
        setVaultStatus({ kind: "locked", unlockable: state.unlockable, error: null, busy: false });
      }
    } catch (cause) {
      const message =
        cause instanceof VaultError && cause.code === "cancelled" ? null : describe(cause);
      setVaultStatus((current) =>
        current.kind === "locked" ? { ...current, busy: false, error: message } : current,
      );
      if (message) {
        throw cause;
      }
    }
  }, [publishUnlocked, vault]);

  const enrollPasskey = useCallback(
    async (options: { mode?: "reuse-or-create" | "create" } = {}) => {
      // The name is only a default for the WebAuthn user identity of a
      // passkey created here; once the header records one (first enrolment,
      // or the userHandle of a reused passkey) the vault keeps using that.
      const userName = active?.username ?? stored.accounts[0]?.username ?? "Lately";
      const result = await vault.enrollPasskey({
        userName,
        userDisplayName: `Lately (${userName})`,
        mode: options.mode,
      });
      setVaultStatus({ kind: "ready", protection: "passkey", passkeyAvailable: true });
      return result;
    },
    [active, stored.accounts, vault],
  );

  const lockVault = useCallback(async () => {
    await enqueue(async () => {
      openedKeyRef.current = null;
      await shell.close().catch(() => {});
      await vault.lock();
    });
    storeKeyRef.current = null;
    setStored({ accounts: [], active: 0 });
    setSession({ status: "idle" });
    const state = await vault.status();
    setVaultStatus({
      kind: "locked",
      unlockable: state.status === "locked" ? state.unlockable : false,
      error: null,
      busy: false,
    });
  }, [enqueue, shell, vault]);

  const resetVault = useCallback(async () => {
    await enqueue(async () => {
      openedKeyRef.current = null;
      await shell.close().catch(() => {});
      try {
        await shell.wipe();
      } catch (cause) {
        console.warn("Lately: storage wipe failed", cause);
      }
      await vault.reset();
    });
    storeKeyRef.current = null;
    setStoreEncrypted(null);
    setStored({ accounts: [], active: 0 });
    setSession({ status: "idle" });
    setVaultStatus({
      kind: "ready",
      protection: null,
      passkeyAvailable: await vault.passkeyAvailable(),
    });
  }, [enqueue, shell, vault]);

  const value = useMemo(
    () => ({
      shell,
      accounts: stored.accounts,
      activeIndex: stored.active,
      active,
      username: active?.username ?? null,
      session,
      connecting,
      error,
      addingAccount,
      setAddingAccount,
      signInEmail,
      signInBasic,
      switchAccount,
      removeAccount,
      takeoverStorage,
      vault: vaultStatus,
      unlockVault,
      enrollPasskey,
      lockVault,
      resetVault,
      storeEncrypted,
    }),
    [
      shell,
      stored,
      active,
      session,
      connecting,
      error,
      addingAccount,
      signInEmail,
      signInBasic,
      switchAccount,
      removeAccount,
      takeoverStorage,
      vaultStatus,
      unlockVault,
      enrollPasskey,
      lockVault,
      resetVault,
      storeEncrypted,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth requires an AuthProvider ancestor");
  }
  return value;
}
